import Image from "next/image";

interface QuoteProps {
  text: string;
  type: "client" | "employee" | "testimonial";
  author?: string;
  role?: string;
  className?: string;
  children?: React.ReactNode;
  image?: {
    src: string;
    alt: string;
  };
}

const SHAPES = [
  "ball-teal.png",
  "ball-purple.png",
  "ball-blue.png",
  "ball-coral.png",
  "cube-teal.png",
  "cube-purple.png",
  "cube-blue.png",
  "cube-coral.png",
  "arch-teal.png",
  "arch-purple.png",
  "arch-blue.png",
  "arch-coral.png",
  "pyramid-teal.png",
  "pyramid-purple.png",
  "pyramid-blue.png",
  "pyramid-coral.png",
] as const;

export default function Quote({
  text,
  type,
  author,
  role,
  className = "",
  children,
  image,
}: QuoteProps) {
  // Use text length to pick a shape deterministically
  const shapeIndex = text.length % SHAPES.length;
  const shape = SHAPES[shapeIndex];

  return (
    <div className={`my-8 md:my-12 relative w-full ${className}`}>
      <div className="relative py-8 md:py-16 px-6 md:px-12 lg:px-32 rounded-3xl bg-card-texture bg-repeat">
        <Image
          src={`/shapes/${shape}`}
          alt=""
          width={200}
          height={200}
          className="w-16 md:w-24 absolute -top-8 md:-top-12 lg:-top-8 z-20 right-12 md:right-4"
          loading="lazy"
        />
        <div
          className="absolute z-20 top-6 md:top-8 left-6 md:left-8 text-6xl md:text-[120px] leading-none opacity-10 font-serif select-none"
          aria-hidden="true"
        >
          &ldquo;
        </div>

        <div className="relative z-20">
          <p className="text-xs md:text-sm uppercase tracking-wide opacity-60 mb-4 md:mb-6">
            {type === "client"
              ? "Working with us"
              : type === "employee"
                ? "Working for us"
                : "What partners say"}
          </p>

          <div>
            <h2 className="text-xl md:text-3xl lg:text-4xl font-normal leading-relaxed">
              &ldquo;{text}&rdquo;
            </h2>
            {(author || children) && (
              <div className="mt-4 md:mt-6 flex items-center gap-4">
                {children}
                {image && (
                  <Image
                    src={image.src}
                    alt={image.alt}
                    width={48}
                    height={48}
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full"
                  />
                )}
                <div className="flex flex-col justify-center">
                  {author && (
                    <p className="text-lg md:text-xl">
                      {author}
                      {role && (
                        <span className="opacity-75 text-base md:text-lg">
                          {" "}
                          · {role}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
